import React, { useState, useEffect } from "react";
import useAxios from "../../utils/useAxios";
import { Link } from "react-router-dom";
import { PARTNER_DASHBOARD_URL } from "../../config";

const BASE_URL = PARTNER_DASHBOARD_URL;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [cities, setCities] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    customers: 0,
    restaurantPartners: 0,
    deliveryPartners: 0,
  });
  const [loading, setLoading] = useState(true);
  const [viewData, setViewData] = useState({});

  // Filter States
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedCity, setSelectedCity] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState({
    id: "",
    email: "",
    password: "",
  });

  const axiosAPI = useAxios();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosAPI.get(`/api/dashboard/all-users`);
      const usersData = response.data.data || [];
      const citiesData = response.data.cities || [];
      const summaryData = response.data.summary || {
        total: usersData.length,
        customers: usersData.filter((u) => u.role === "Customer").length,
        restaurantPartners: usersData.filter(
          (u) => u.role === "Restaurant Partner",
        ).length,
        deliveryPartners: usersData.filter((u) => u.role === "Delivery Partner")
          .length,
      };

      setUsers(usersData);
      setCities(citiesData);
      setSummary(summaryData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleView = async (user) => {
    setViewData(user);
    showModal("viewUserModal");
    if (user.role === "Delivery Partner") {
      try {
        const res = await axiosAPI.get(
          `/api/delivery-partner/admin/partners/${user.id}`,
        );
        if (res.data && res.data.success) {
          setViewData({ ...user, ...res.data.data, role: "Delivery Partner" });
        }
      } catch (err) {
        console.warn("Error loading rider full details:", err);
      }
    }
  };

  const handleUpdatePartnerStatus = async (partnerId, newStatus) => {
    if (
      !window.confirm(
        `Are you sure you want to change rider status to ${newStatus}?`,
      )
    ) {
      return;
    }
    try {
      const res = await axiosAPI.patch(
        `/api/delivery-partner/admin/partners/${partnerId}/status`,
        { status: newStatus },
      );
      if (res.data && res.data.success) {
        if (window.notifyMessage) {
          window.notifyMessage(
            "Success",
            res.data.message || `Status changed to ${newStatus}`,
          );
        } else {
          alert(`Success: Status changed to ${newStatus}`);
        }
        setViewData((prev) => ({ ...prev, status: newStatus }));
        fetchUsers();
      }
    } catch (err) {
      console.error("Status update error:", err);
      const msg =
        err.response?.data?.message || err.message || "Failed to update status";
      if (window.notifyMessage) {
        window.notifyMessage("Error", msg, "danger");
      } else {
        alert(`Error: ${msg}`);
      }
    }
  };

  const handleUpdatePartnerBankStatus = async (partnerId, newBankStatus) => {
    try {
      const res = await axiosAPI.patch(
        `/api/delivery-partner/admin/partners/${partnerId}/bank-status`,
        { bank_verification_status: newBankStatus },
      );
      if (res.data && res.data.success) {
        if (window.notifyMessage) {
          window.notifyMessage("Success", res.data.message);
        } else {
          alert(`Success: Bank verification set to ${newBankStatus}`);
        }
        setViewData((prev) => ({
          ...prev,
          bank_verification_status: newBankStatus,
          bankDetails: {
            ...(prev.bankDetails || {}),
            bankVerificationStatus: newBankStatus,
          },
        }));
        fetchUsers();
      }
    } catch (err) {
      console.error("Bank status update error:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to update bank status";
      if (window.notifyMessage) {
        window.notifyMessage("Error", msg, "danger");
      } else {
        alert(`Error: ${msg}`);
      }
    }
  };

  const formatImgUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${BASE_URL.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const openEditModal = (userId) => {
    const userToEdit = users.find((user) => user.id === userId);
    if (userToEdit) {
      setFormData({
        id: userToEdit.id,
        email: userToEdit.email,
        password: "",
      });
      showModal("editUserModal");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const userId = formData.id;
    const dataToUpdate = {
      email: formData.email,
      password: formData.password || undefined,
    };
    if (!dataToUpdate.password) {
      delete dataToUpdate.password;
    }

    try {
      await axiosAPI.put(`/api/users/update-user/${userId}`, dataToUpdate);
      window.notifyMessage("Success", "User updated successfully");
      hideModal("editUserModal");
      fetchUsers();
    } catch (error) {
      if (error.response?.data?.message) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage("Error", "Error updating the user.", "danger");
      }
    }
  };

  function showModal(id) {
    const modal_el = document.querySelector("#" + id);
    let modal_obj = window.bootstrap.Modal.getInstance(modal_el);
    if (modal_obj == null) {
      modal_obj = new window.bootstrap.Modal(modal_el, { backdrop: "static" });
    }
    modal_obj.show();
  }

  function hideModal(id) {
    const modal_el = document.querySelector("#" + id);
    const modal_obj = window.bootstrap.Modal.getInstance(modal_el);
    if (modal_obj) modal_obj.hide();
  }

  // Filtered Users List
  const filteredUsers = users.filter((user) => {
    if (selectedRole !== "All" && user.role !== selectedRole) {
      return false;
    }
    if (selectedCity !== "All" && user.city !== selectedCity) {
      return false;
    }
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      const nameMatch = (user.name || "").toLowerCase().includes(q);
      const emailMatch = (user.email || "").toLowerCase().includes(q);
      const phoneMatch = (user.phone || "").toLowerCase().includes(q);
      const cityMatch = (user.city || "").toLowerCase().includes(q);
      if (!nameMatch && !emailMatch && !phoneMatch && !cityMatch) {
        return false;
      }
    }
    return true;
  });

  const getRoleBadge = (role) => {
    switch (role) {
      case "Customer":
        return "badge bg-info text-white";
      case "Restaurant Partner":
        return "badge bg-success text-white";
      case "Delivery Partner":
        return "badge bg-warning text-dark";
      default:
        return "badge bg-secondary text-white";
    }
  };

  const handleImpersonateLogin = async (restaurantId) => {
    try {
      const response = await axiosAPI.get(
        `/api/dashboard/impersonate-partner/${restaurantId}`,
      );
      if (response.data && response.data.accessToken) {
        const partnerToken = response.data.accessToken;
        const partnerAppUrl = `${BASE_URL}/login?token=${partnerToken}`;
        window.open(partnerAppUrl, "_blank");
        if (window.notifyMessage) {
          window.notifyMessage(
            "Success",
            "Opening Restaurant Partner Dashboard...",
          );
        }
      } else {
        if (window.notifyMessage) {
          window.notifyMessage(
            "Error",
            "Could not generate login token.",
            "danger",
          );
        }
      }
    } catch (error) {
      console.error("Partner direct login error:", error);
      if (window.notifyMessage) {
        window.notifyMessage(
          "Error",
          "Failed to login to restaurant partner portal.",
          "danger",
        );
      }
    }
  };

  return (
    <div className="container">
      <div className="page-inner">
        {/* Top Summary Cards */}
        <div className="row mb-4">
          <div className="col-sm-6 col-md-3">
            <div
              className={`card card-stats card-round cursor-pointer ${selectedRole === "All" ? "border border-2 border-primary" : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedRole("All")}
            >
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-icon">
                    <div className="icon-big text-center icon-primary bubble-shadow-small">
                      <i className="fas fa-users"></i>
                    </div>
                  </div>
                  <div className="col col-stats ms-3 ms-sm-0">
                    <div className="numbers">
                      <p className="card-category">All Users</p>
                      <h4 className="card-title">{summary.total}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-md-3">
            <div
              className={`card card-stats card-round ${selectedRole === "Customer" ? "border border-2 border-info" : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedRole("Customer")}
            >
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-icon">
                    <div className="icon-big text-center icon-info bubble-shadow-small">
                      <i className="fas fa-user font-weight-bold"></i>
                    </div>
                  </div>
                  <div className="col col-stats ms-3 ms-sm-0">
                    <div className="numbers">
                      <p className="card-category">Customers</p>
                      <h4 className="card-title">{summary.customers}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-md-3">
            <div
              className={`card card-stats card-round ${selectedRole === "Restaurant Partner" ? "border border-2 border-success" : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedRole("Restaurant Partner")}
            >
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-icon">
                    <div className="icon-big text-center icon-success bubble-shadow-small">
                      <i className="fas fa-utensils"></i>
                    </div>
                  </div>
                  <div className="col col-stats ms-3 ms-sm-0">
                    <div className="numbers">
                      <p className="card-category">Restaurants</p>
                      <h4 className="card-title">
                        {summary.restaurantPartners}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-md-3">
            <div
              className={`card card-stats card-round ${selectedRole === "Delivery Partner" ? "border border-2 border-warning" : ""}`}
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedRole("Delivery Partner")}
            >
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-icon">
                    <div className="icon-big text-center icon-warning bubble-shadow-small">
                      <i className="fas fa-motorcycle"></i>
                    </div>
                  </div>
                  <div className="col col-stats ms-3 ms-sm-0">
                    <div className="numbers">
                      <p className="card-category">Delivery Partners</p>
                      <h4 className="card-title">{summary.deliveryPartners}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit User Modal */}
        <div
          className="modal fade"
          id="editUserModal"
          tabIndex="-1"
          aria-labelledby="editUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="editUserModalLabel">
                  Edit User
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      Email ID
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="password"
                      placeholder="Enter New Password (optional)"
                      value={formData.password}
                      onChange={handleChange}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-round btn-sm"
                  >
                    Update User
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* View User Full Details Modal */}
        <div
          className="modal fade"
          id="viewUserModal"
          tabIndex="-1"
          aria-labelledby="viewUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow border-0">
              <div className="modal-header bg-dark text-white py-3">
                <h5
                  className="modal-title flex-grow-1 fw-bold"
                  id="viewUserModalLabel"
                >
                  <i className="fas fa-id-card me-2 text-warning"></i>
                  {viewData.role === "Restaurant Partner"
                    ? "Restaurant Partner Details"
                    : viewData.role === "Delivery Partner"
                      ? "Delivery Partner Details"
                      : "Customer Profile Details"}
                </h5>
                {viewData.role && (
                  <span
                    className={`${getRoleBadge(viewData.role)} me-3 px-3 py-2 fs-6`}
                  >
                    {viewData.role}
                  </span>
                )}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>

              <div className="modal-body p-4">
                {/* 1. RESTAURANT PARTNER FULL DETAILS */}
                {viewData.role === "Restaurant Partner" ? (
                  <div>
                    {/* Header Banner & Impersonate Action */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center bg-light p-3 rounded mb-4 border">
                      <div className="d-flex alignItems-center gap-3">
                        {viewData.storefront_photo || viewData.logo_url ? (
                          <img
                            src={viewData.storefront_photo || viewData.logo_url}
                            alt="Restaurant Logo"
                            className="rounded border"
                            style={{
                              width: "64px",
                              height: "64px",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            className="rounded bg-success text-white d-flex align-items-center justify-content-center fw-bold fs-3"
                            style={{ width: "64px", height: "64px" }}
                          >
                            🏪
                          </div>
                        )}
                        <div>
                          <h4 className="fw-bold text-dark m-0">
                            {viewData.restaurant_name ||
                              viewData.name ||
                              "Restaurant"}
                          </h4>
                          <span className="badge bg-secondary text-uppercase me-2 mt-1">
                            Status: {viewData.status || "Draft"}
                          </span>
                          <span className="badge bg-outline-dark text-dark mt-1">
                            Partner ID: #{viewData.partner_id || viewData.id}
                          </span>
                        </div>
                      </div>

                      <button
                        className="btn btn-success btn-round btn-sm mt-3 mt-md-0 fw-bold shadow-sm"
                        onClick={() => handleImpersonateLogin(viewData.id)}
                        title="Login directly into partner dashboard"
                      >
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Login to Partner Dashboard
                      </button>
                    </div>

                    <div className="row gy-3">
                      <div className="col-md-6">
                        <small className="text-muted text-uppercase fw-bold">
                          Owner Name
                        </small>
                        <div className="fw-bold text-dark fs-6">
                          {viewData.owner_name || viewData.name || "-"}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <small className="text-muted text-uppercase fw-bold">
                          Owner Phone / Email
                        </small>
                        <div className="fw-bold text-dark">
                          {viewData.phone || viewData.owner_phone || "-"}
                        </div>
                        <div className="text-muted small">
                          {viewData.email || viewData.owner_email || "-"}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <small className="text-muted text-uppercase fw-bold">
                          City
                        </small>
                        <div className="fw-semibold text-dark">
                          {viewData.city || "-"}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <small className="text-muted text-uppercase fw-bold">
                          Pincode
                        </small>
                        <div className="fw-semibold text-dark">
                          {viewData.pincode || "-"}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <small className="text-muted text-uppercase fw-bold">
                          Landmark
                        </small>
                        <div className="fw-semibold text-dark">
                          {viewData.landmark || "-"}
                        </div>
                      </div>

                      <div className="col-md-12">
                        <small className="text-muted text-uppercase fw-bold">
                          Complete Address
                        </small>
                        <div className="fw-semibold text-dark bg-light p-2 rounded border">
                          {viewData.complete_address || viewData.address || "-"}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <small className="text-muted text-uppercase fw-bold">
                          FSSAI License No.
                        </small>
                        <div className="fw-bold text-success">
                          {viewData.fssai_no || viewData.fssai_number || "-"}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <small className="text-muted text-uppercase fw-bold">
                          Commission Rate
                        </small>
                        <div className="fw-bold text-dark">
                          {viewData.commission_rate
                            ? `${viewData.commission_rate}%`
                            : "-"}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <small className="text-muted text-uppercase fw-bold">
                          Service & Food Type
                        </small>
                        <div className="fw-semibold text-dark">
                          {viewData.service_type || "both"} |{" "}
                          {viewData.veg_type || "pureveg"}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <small className="text-muted text-uppercase fw-bold">
                          Operating Hours
                        </small>
                        <div className="fw-semibold text-dark">
                          {viewData.open_time || "09:00 AM"} -{" "}
                          {viewData.close_time || "11:00 PM"}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <small className="text-muted text-uppercase fw-bold">
                          Account Created
                        </small>
                        <div className="fw-semibold text-dark">
                          {viewData.created_at
                            ? new Date(viewData.created_at).toLocaleString(
                                "en-GB",
                              )
                            : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : viewData.role === "Delivery Partner" ? (
                  /* 2. DELIVERY PARTNER FULL DETAILS & CONTROL PANEL */
                  <div>
                    {/* Admin Status Control Header Bar */}
                    <div className="alert alert-secondary d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4 shadow-sm border">
                      <div>
                        <span className="fw-bold me-2 text-dark">
                          Rider Account Control:
                        </span>
                        <span className="badge bg-dark fs-6">
                          {viewData.status || "ONBOARDING"}
                        </span>
                      </div>
                      <div className="btn-group mt-2 mt-md-0">
                        <button
                          className={`btn btn-sm ${
                            viewData.status === "PROFILE_ACTIVE"
                              ? "btn-success active"
                              : "btn-outline-success"
                          }`}
                          onClick={() =>
                            handleUpdatePartnerStatus(
                              viewData.id,
                              "PROFILE_ACTIVE",
                            )
                          }
                        >
                          <i className="fas fa-check-circle me-1"></i> Approve &
                          Activate
                        </button>
                        <button
                          className={`btn btn-sm ${
                            viewData.status === "PENDING"
                              ? "btn-warning active"
                              : "btn-outline-warning"
                          }`}
                          onClick={() =>
                            handleUpdatePartnerStatus(viewData.id, "PENDING")
                          }
                        >
                          <i className="fas fa-clock me-1"></i> Mark Pending
                        </button>
                        <button
                          className={`btn btn-sm ${
                            viewData.status === "SUSPENDED"
                              ? "btn-danger active"
                              : "btn-outline-danger"
                          }`}
                          onClick={() =>
                            handleUpdatePartnerStatus(viewData.id, "SUSPENDED")
                          }
                        >
                          <i className="fas fa-ban me-1"></i> Suspend Rider
                        </button>
                        <button
                          className={`btn btn-sm ${
                            viewData.status === "BLOCKED"
                              ? "btn-dark active"
                              : "btn-outline-dark"
                          }`}
                          onClick={() =>
                            handleUpdatePartnerStatus(viewData.id, "BLOCKED")
                          }
                        >
                          <i className="fas fa-user-slash me-1"></i> Block Rider
                        </button>
                      </div>
                    </div>

                    <div className="row g-3">
                      {/* Left: Info */}
                      <div className="col-md-6">
                        <div className="card h-100 border p-3">
                          <h6 className="fw-bold text-primary mb-3">
                            <i className="fas fa-id-badge me-2"></i> Rider
                            Information
                          </h6>
                          <div className="mb-2">
                            <small className="text-muted text-uppercase fw-bold">
                              Name & Phone
                            </small>
                            <div className="fw-bold text-dark fs-6">
                              {viewData.name || "Delivery Partner"} (
                              {viewData.phone})
                            </div>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted text-uppercase fw-bold">
                              Email / Emergency
                            </small>
                            <div className="text-dark">
                              {viewData.email || "N/A"} | Emergency:{" "}
                              {viewData.emergency_contact || "N/A"}
                            </div>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted text-uppercase fw-bold">
                              City & Operating Zone
                            </small>
                            <div className="fw-semibold text-dark">
                              {viewData.city || viewData.zone || "N/A"}
                            </div>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted text-uppercase fw-bold">
                              Vehicle & Registration
                            </small>
                            <div className="fw-semibold text-dark">
                              {viewData.vehicle || "Motorcycle"}{" "}
                              {viewData.vehicle_number || viewData.vehicleNumber
                                ? `(${viewData.vehicle_number || viewData.vehicleNumber})`
                                : ""}
                            </div>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted text-uppercase fw-bold">
                              Address
                            </small>
                            <div className="small text-dark bg-light p-2 rounded border">
                              {viewData.address ||
                                viewData.bag_delivery_address ||
                                "N/A"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Bank Details & Verification */}
                      <div className="col-md-6">
                        <div className="card h-100 border p-3">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold text-success mb-0">
                              <i className="fas fa-university me-2"></i> Bank
                              Details & Payouts
                            </h6>
                            <span
                              className={`badge ${
                                (viewData.bank_verification_status ||
                                  viewData.bankDetails
                                    ?.bankVerificationStatus) === "VERIFIED"
                                  ? "bg-success"
                                  : (viewData.bank_verification_status ||
                                        viewData.bankDetails
                                          ?.bankVerificationStatus) ===
                                      "REJECTED"
                                    ? "bg-danger"
                                    : "bg-warning text-dark"
                              }`}
                            >
                              {viewData.bank_verification_status ||
                                viewData.bankDetails?.bankVerificationStatus ||
                                "PENDING"}
                            </span>
                          </div>

                          <table className="table table-sm table-borderless mb-2">
                            <tbody>
                              <tr>
                                <td className="text-muted fw-bold">
                                  Bank Name:
                                </td>
                                <td className="fw-bold">
                                  {viewData.bank_name ||
                                    viewData.bankDetails?.bankName ||
                                    "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td className="text-muted fw-bold">
                                  Account Holder:
                                </td>
                                <td>
                                  {viewData.account_holder_name ||
                                    viewData.bankDetails?.accountHolderName ||
                                    "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td className="text-muted fw-bold">
                                  Account Number:
                                </td>
                                <td className="fw-bold font-monospace">
                                  {viewData.account_number ||
                                    viewData.bankDetails?.accountNumber ||
                                    "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td className="text-muted fw-bold">
                                  IFSC Code:
                                </td>
                                <td className="fw-bold font-monospace">
                                  {viewData.ifsc_code ||
                                    viewData.bankDetails?.ifscCode ||
                                    "N/A"}
                                </td>
                              </tr>
                            </tbody>
                          </table>

                          <div className="d-flex align-items-center gap-2 pt-2 border-top mt-auto">
                            <span className="small text-muted fw-bold me-auto">
                              Verify Bank:
                            </span>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() =>
                                handleUpdatePartnerBankStatus(
                                  viewData.id,
                                  "VERIFIED",
                                )
                              }
                            >
                              <i className="fas fa-check me-1"></i> Approve
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                handleUpdatePartnerBankStatus(
                                  viewData.id,
                                  "REJECTED",
                                )
                              }
                            >
                              <i className="fas fa-times me-1"></i> Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Documents & Image Previews Grid */}
                    <h6 className="fw-bold mt-4 mb-2 text-dark">
                      <i className="fas fa-file-image me-2"></i> Onboarding
                      Document Previews
                    </h6>
                    <div className="row g-2">
                      {/* Selfie */}
                      <div className="col-md-4 col-6 text-center">
                        <div className="border rounded p-2 bg-light h-100">
                          <small className="fw-bold d-block mb-1">
                            Selfie Photo
                          </small>
                          {viewData.selfie_url || viewData.selfieUrl ? (
                            <a
                              href={formatImgUrl(
                                viewData.selfie_url || viewData.selfieUrl,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={formatImgUrl(
                                  viewData.selfie_url || viewData.selfieUrl,
                                )}
                                alt="Selfie"
                                className="img-fluid rounded border"
                                style={{
                                  maxHeight: "120px",
                                  objectFit: "cover",
                                }}
                              />
                            </a>
                          ) : (
                            <span className="text-muted small">
                              Not Uploaded
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Driving License */}
                      <div className="col-md-4 col-6 text-center">
                        <div className="border rounded p-2 bg-light h-100">
                          <small className="fw-bold d-block mb-1">
                            Driving License
                          </small>
                          {viewData.driving_license_url ||
                          viewData.drivingLicenseUrl ? (
                            <a
                              href={formatImgUrl(
                                viewData.driving_license_url ||
                                  viewData.drivingLicenseUrl,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={formatImgUrl(
                                  viewData.driving_license_url ||
                                    viewData.drivingLicenseUrl,
                                )}
                                alt="Driving License"
                                className="img-fluid rounded border"
                                style={{
                                  maxHeight: "120px",
                                  objectFit: "contain",
                                }}
                              />
                            </a>
                          ) : (
                            <span className="text-muted small">
                              Not Uploaded
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Aadhaar Front */}
                      <div className="col-md-4 col-6 text-center">
                        <div className="border rounded p-2 bg-light h-100">
                          <small className="fw-bold d-block mb-1">
                            Aadhaar Front
                          </small>
                          {viewData.aadhaar_front_url ||
                          viewData.aadhaarFrontUrl ? (
                            <a
                              href={formatImgUrl(
                                viewData.aadhaar_front_url ||
                                  viewData.aadhaarFrontUrl,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={formatImgUrl(
                                  viewData.aadhaar_front_url ||
                                    viewData.aadhaarFrontUrl,
                                )}
                                alt="Aadhaar Front"
                                className="img-fluid rounded border"
                                style={{
                                  maxHeight: "120px",
                                  objectFit: "contain",
                                }}
                              />
                            </a>
                          ) : (
                            <span className="text-muted small">
                              Not Uploaded
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Aadhaar Back */}
                      <div className="col-md-6 col-6 text-center">
                        <div className="border rounded p-2 bg-light h-100">
                          <small className="fw-bold d-block mb-1">
                            Aadhaar Back
                          </small>
                          {viewData.aadhaar_back_url ||
                          viewData.aadhaarBackUrl ? (
                            <a
                              href={formatImgUrl(
                                viewData.aadhaar_back_url ||
                                  viewData.aadhaarBackUrl,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={formatImgUrl(
                                  viewData.aadhaar_back_url ||
                                    viewData.aadhaarBackUrl,
                                )}
                                alt="Aadhaar Back"
                                className="img-fluid rounded border"
                                style={{
                                  maxHeight: "120px",
                                  objectFit: "contain",
                                }}
                              />
                            </a>
                          ) : (
                            <span className="text-muted small">
                              Not Uploaded
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Cancelled Cheque */}
                      <div className="col-md-6 col-6 text-center">
                        <div className="border rounded p-2 bg-light h-100">
                          <small className="fw-bold d-block mb-1">
                            Cancelled Cheque (Bank)
                          </small>
                          {viewData.cancelled_cheque_url ||
                          viewData.bankDetails?.cancelledChequeUrl ? (
                            <a
                              href={formatImgUrl(
                                viewData.cancelled_cheque_url ||
                                  viewData.bankDetails?.cancelledChequeUrl,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={formatImgUrl(
                                  viewData.cancelled_cheque_url ||
                                    viewData.bankDetails?.cancelledChequeUrl,
                                )}
                                alt="Cancelled Cheque"
                                className="img-fluid rounded border"
                                style={{
                                  maxHeight: "120px",
                                  objectFit: "contain",
                                }}
                              />
                            </a>
                          ) : (
                            <span className="text-muted small">
                              Not Uploaded
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* 3. CUSTOMER FULL DETAILS */
                  <div className="row gy-3">
                    <div className="col-md-6">
                      <small className="text-muted text-uppercase fw-bold">
                        Customer Name
                      </small>
                      <div className="fw-bold text-dark fs-6">
                        {viewData.name || viewData.full_name || "-"}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted text-uppercase fw-bold">
                        Email ID
                      </small>
                      <div className="fw-semibold text-dark">
                        {viewData.email || "-"}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted text-uppercase fw-bold">
                        Phone Number
                      </small>
                      <div className="fw-semibold text-dark">
                        {viewData.phone || viewData.phone_number || "-"}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <small className="text-muted text-uppercase fw-bold">
                        Gender
                      </small>
                      <div className="fw-semibold text-dark">
                        {viewData.gender || "-"}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted text-uppercase fw-bold">
                        City
                      </small>
                      <div className="fw-semibold text-dark">
                        {viewData.city || "-"}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted text-uppercase fw-bold">
                        State
                      </small>
                      <div className="fw-semibold text-dark">
                        {viewData.state || "-"}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted text-uppercase fw-bold">
                        Country
                      </small>
                      <div className="fw-semibold text-dark">
                        {viewData.country || "-"}
                      </div>
                    </div>

                    <div className="col-md-12">
                      <small className="text-muted text-uppercase fw-bold">
                        Address
                      </small>
                      <div className="fw-semibold text-dark bg-light p-2 rounded border">
                        {viewData.address || "-"}
                      </div>
                    </div>

                    <div className="col-md-12">
                      <small className="text-muted text-uppercase fw-bold">
                        Account Created
                      </small>
                      <div className="fw-semibold text-dark">
                        {viewData.created_at
                          ? new Date(viewData.created_at).toLocaleString(
                              "en-GB",
                            )
                          : "-"}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer bg-light">
                {viewData.role === "Restaurant Partner" && (
                  <button
                    type="button"
                    className="btn btn-success btn-sm me-auto fw-bold"
                    onClick={() => handleImpersonateLogin(viewData.id)}
                  >
                    <i className="fas fa-sign-in-alt me-1"></i> Direct Login to
                    Partner
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Card with Datatable & Filters */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                  <div>
                    <h4 className="card-title m-0">
                      User Directory (<span>{filteredUsers.length}</span> /{" "}
                      {users.length})
                    </h4>
                  </div>

                  {/* Filter Toolbar */}
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {/* Role Filter */}
                    <div>
                      <select
                        className="form-select form-select-sm"
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        style={{ minWidth: "150px" }}
                      >
                        <option value="All">All Roles</option>
                        <option value="Customer">Customers</option>
                        <option value="Restaurant Partner">
                          Restaurant Partners
                        </option>
                        <option value="Delivery Partner">
                          Delivery Partners
                        </option>
                      </select>
                    </div>

                    {/* City Filter */}
                    <div>
                      <select
                        className="form-select form-select-sm"
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                        style={{ minWidth: "140px" }}
                      >
                        <option value="All">
                          All Cities ({cities.length})
                        </option>
                        {cities.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Search Bar */}
                    <div>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">
                          <i className="fas fa-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search name, email, phone..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Clear Filters Button */}
                    {(selectedRole !== "All" ||
                      selectedCity !== "All" ||
                      searchQuery !== "") && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setSelectedRole("All");
                          setSelectedCity("All");
                          setSearchQuery("");
                        }}
                        title="Reset Filters"
                      >
                        <i className="fas fa-undo me-1"></i> Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="display table table-striped table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>User Role</th>
                        <th>Email / Phone</th>
                        <th>City</th>
                        <th>Created At</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div
                              className="spinner-border spinner-border-sm text-primary me-2"
                              role="status"
                            ></div>
                            Loading Users Directory...
                          </td>
                        </tr>
                      ) : filteredUsers.length === 0 ? (
                        <tr>
                          <td
                            colSpan="6"
                            className="text-center text-muted py-4"
                          >
                            No users found matching your filters.
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((user, idx) => {
                          const formattedDate = user.created_at
                            ? new Date(user.created_at).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                },
                              )
                            : "-";

                          return (
                            <tr
                              key={
                                user.id ? `${user.role}-${user.id}-${idx}` : idx
                              }
                            >
                              <td>
                                <div className="fw-bold">
                                  {user.name || "N/A"}
                                </div>
                              </td>

                              <td>
                                <span className={getRoleBadge(user.role)}>
                                  {user.role}
                                </span>
                              </td>

                              <td>
                                <div className="text-dark small fw-semibold">
                                  {user.email || "-"}
                                </div>
                                <div className="text-muted small">
                                  {user.phone || ""}
                                </div>
                              </td>

                              <td>
                                <span className="badge bg-light text-dark border">
                                  {user.city || "N/A"}
                                </span>
                              </td>

                              <td>{formattedDate}</td>

                              <td className="text-end">
                                {user.role === "Restaurant Partner" && (
                                  <button
                                    className="btn btn-icon btn-round btn-success btn-sm me-2"
                                    onClick={() =>
                                      handleImpersonateLogin(user.id)
                                    }
                                    title="Login as Restaurant Partner"
                                  >
                                    <i className="fas fa-sign-in-alt"></i>
                                  </button>
                                )}

                                <button
                                  className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                  onClick={() => openEditModal(user.id)}
                                  title="Edit User"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>

                                <button
                                  className="btn btn-icon btn-round btn-info btn-sm"
                                  data-bs-toggle="modal"
                                  data-bs-target="#viewUserModal"
                                  onClick={() => handleView(user)}
                                  title="View User Details"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
